import { BernieGraphqlClient, FetchPolicy, RequestOptions } from 'bernie-graphql';
import * as generated from './types';
import * as operations from './operations';

export interface OperationResult<T> {
  data: T;
  errors?: [];
  extensions: any;
  headers: Headers;
}

function isFetchPolicy(requestOptions: RequestOptions | FetchPolicy): requestOptions is FetchPolicy {
      return typeof requestOptions === 'string';
}

export class ApiOperations {
    public constructor(private client: BernieGraphqlClient) {
      //
    }

    public addPropertyToCart(
      variables: generated.AddPropertyToCart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.AddPropertyToCart.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.addPropertyToCartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.AddPropertyToCart.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public createMultiItemSession(
      variables: generated.CreateMultiItemSession.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.CreateMultiItemSession.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.createMultiItemSessionQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.CreateMultiItemSession.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public createSessionCart(
      variables: generated.CreateSessionCart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.CreateSessionCart.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.createSessionCartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.CreateSessionCart.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public emptyCart(
      variables: generated.EmptyCart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.EmptyCart.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.emptyCartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.EmptyCart.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public federatedCart(
      variables: generated.FederatedCart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.FederatedCart.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.federatedCartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.FederatedCart.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public cart(
      variables: generated.Cart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.Cart.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.cartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.Cart.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public initiateMultiItemSession(
      variables: generated.InitiateMultiItemSession.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.InitiateMultiItemSession.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.initiateMultiItemSessionQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.InitiateMultiItemSession.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyLocationScoreInfo(
      variables: generated.PropertyLocationScoreInfo.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyLocationScoreInfo.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyLocationScoreInfoQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyLocationScoreInfo.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public lodgingPwaPropertySearch(
      variables: generated.LodgingPwaPropertySearch.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.LodgingPwaPropertySearch.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.lodgingPwaPropertySearchQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.LodgingPwaPropertySearch.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public lodgingPwaPropertySearchSortAndFilter(
      variables: generated.LodgingPwaPropertySearchSortAndFilter.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.LodgingPwaPropertySearchSortAndFilter.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.lodgingPwaPropertySearchSortAndFilterQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.LodgingPwaPropertySearchSortAndFilter.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public prepareCheckoutByCartId(
      variables: generated.PrepareCheckoutByCartId.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PrepareCheckoutByCartId.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.prepareCheckoutByCartIdQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PrepareCheckoutByCartId.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public prepareCheckout(
      variables: generated.PrepareCheckout.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PrepareCheckout.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.prepareCheckoutQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PrepareCheckout.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyAvailabilityCheck(
      variables: generated.PropertyAvailabilityCheck.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyAvailabilityCheck.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyAvailabilityCheckQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyAvailabilityCheck.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyInfoPageLayout(
      variables: generated.PropertyInfoPageLayout.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyInfoPageLayout.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyInfoPageLayoutQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyInfoPageLayout.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyRoomScoreInfo(
      variables: generated.PropertyRoomScoreInfo.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyRoomScoreInfo.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyRoomScoreInfoQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyRoomScoreInfo.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyOffers(
      variables: generated.PropertyOffers.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyOffers.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyOffersQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyOffers.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertyRateCalendar(
      variables: generated.PropertyRateCalendar.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertyRateCalendar.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertyRateCalendarQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertyRateCalendar.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public propertySearchTravelAds(
      variables: generated.PropertySearchTravelAds.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.PropertySearchTravelAds.Query>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .query(
          {
            query: operations.propertySearchTravelAdsQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.PropertySearchTravelAds.Query;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public removeItemFromCart(
      variables: generated.RemoveItemFromCart.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.RemoveItemFromCart.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.removeItemFromCartQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.RemoveItemFromCart.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public selectProducts(
      variables: generated.SelectProducts.Variables,
      options?: FetchPolicy | RequestOptions
    ): Promise<OperationResult<generated.SelectProducts.Mutation>> {
      let requestOptions: RequestOptions;
      let fetchPolicy: FetchPolicy;

      if(options){
        requestOptions = isFetchPolicy(options) ? null : options;
        fetchPolicy = isFetchPolicy(options) ? options : requestOptions.fetchPolicy;
      }

      return this.client
        .mutate(
          {
            mutation: operations.selectProductsQuery,
            variables,
            fetchPolicy
          },
          requestOptions
        )
          .then(result => {
            const body = result.body;
            const data = body.data as generated.SelectProducts.Mutation;
            const extensions = body.extensions;
            const errors = body.errors;
            const headers = result.headers;

            return { data, errors, extensions, headers };
          });
    }

    public static withClient(client: BernieGraphqlClient) {
      return new ApiOperations(client);
    }
}
